import { useState } from 'react'

import { t } from '../../../../../../../common/i18n.js'
import type { Language } from '../../../../../../../common/types/i18n.js'
import { Button } from '../../../../../../components/button/button.js'
import { Info, Pencil } from '../../../../../../components/icon/solar-icons.js'
import { Section, SectionHeader } from '../../../../../../components/section/section.js'
import { Tooltip } from '../../../../../../components/tooltip/tooltip.js'
import type { VacationStatBreakdown } from './time-away-derive.js'

interface TipRow {
    label: string
    value: string
}

interface TimeAwayStatsCardProps {
    breakdown: VacationStatBreakdown
    year: number
    lang: Language
    /** Admin-only "Muuda" — opens the balance-correction modal (TASK-567). */
    onAdjust?: () => void
}

// Hero numbers for the main vacation balance, Humaans-style: the balance
// available through year-end, and the balance earned as of today. Each ⓘ
// opens a breakdown tooltip showing the arithmetic.
export const TimeAwayStatsCard = ({ breakdown, year, lang, onAdjust }: TimeAwayStatsCardProps) => {
    const view = t.portal.timeOff.view
    const tBal = t.portal.timeOff.balance
    const [openTip, setOpenTip] = useState<'remaining' | 'balance' | null>(null)

    const day = (n: number) => view.dayCount(n)(lang)

    // Signed "+ 3 päeva" / "− 3 päeva" for the admin-correction rows.
    const signed = (n: number) => `${n < 0 ? '−' : '+'} ${day(Math.abs(n))}`
    const hasAdjustments = breakdown.adjustments !== 0

    // "Available until 31 Dec": allowance (+ carryover ± corrections) − taken − booked ahead.
    const remainingRows: TipRow[] = [
        { label: view.allowance(lang), value: day(breakdown.allowance) },
        ...(breakdown.carryover > 0
            ? [{ label: tBal.carryover(lang), value: `+ ${day(breakdown.carryover)}` }]
            : []),
        ...(hasAdjustments
            ? [{ label: view.adjustments(lang), value: signed(breakdown.adjustments) }]
            : []),
        ...(breakdown.carryover > 0 || hasAdjustments
            ? [{ label: view.totalAllowance(lang), value: `= ${day(breakdown.totalAllowance)}` }]
            : []),
        { label: tBal.used(lang), value: `− ${day(breakdown.usedPast)}` },
        { label: view.upcomingBooked(lang), value: `− ${day(breakdown.upcomingApproved)}` },
        // Days awaiting approval are reserved too — without this row the
        // arithmetic wouldn't sum to the hero number while a request is pending.
        ...(breakdown.pending > 0
            ? [
                  {
                      label: t.portal.timeOff.status.pending(lang),
                      value: `− ${day(breakdown.pending)}`,
                  },
              ]
            : []),
        { label: tBal.available(lang), value: `= ${day(breakdown.remaining)}` },
    ]

    // "Balance as of today": the year's norm earned evenly to date, plus
    // carryover (fully earned last year), minus days actually taken —
    // booked-ahead leave hasn't been taken yet so it is NOT subtracted.
    const balanceRows: TipRow[] = [
        {
            label: view.accruedSince(breakdown.accruedSince)(lang),
            value: day(breakdown.accruedToDate),
        },
        ...(breakdown.carryover > 0
            ? [{ label: tBal.carryover(lang), value: `+ ${day(breakdown.carryover)}` }]
            : []),
        ...(hasAdjustments
            ? [{ label: view.adjustments(lang), value: signed(breakdown.adjustments) }]
            : []),
        ...(breakdown.carryover > 0 || hasAdjustments
            ? [{ label: view.totalAccrued(lang), value: `= ${day(breakdown.totalAccrued)}` }]
            : []),
        { label: tBal.used(lang), value: `− ${day(breakdown.takenThisYear)}` },
        { label: view.balanceLabel(lang), value: `= ${day(breakdown.balanceAsOfToday)}` },
    ]

    const footerRows: TipRow[] = [
        { label: t.portal.timeOff.policy(lang), value: breakdown.policyName },
        { label: t.portal.timeOff.holidays.title(lang), value: view.holidayCountry(lang) },
    ]

    const renderTip = (rows: TipRow[]) => (
        <div className="time-away__tip">
            <div className="time-away__tip-header">
                <span className="time-away__tip-emoji">{breakdown.emoji}</span>
                {breakdown.leaveTypeName}
            </div>
            {rows.map((row) => (
                <div key={row.label} className="time-away__tip-row">
                    <span className="time-away__tip-label">{row.label}</span>
                    <span className="time-away__tip-value">{row.value}</span>
                </div>
            ))}
            <div className="time-away__tip-divider" />
            {footerRows.map((row) => (
                <div key={row.label} className="time-away__tip-row">
                    <span className="time-away__tip-label">{row.label}</span>
                    <span className="time-away__tip-value">{row.value}</span>
                </div>
            ))}
        </div>
    )

    const stats = [
        {
            key: 'remaining' as const,
            value: breakdown.remaining,
            caption: view.availableUntil(year)(lang),
            rows: remainingRows,
        },
        {
            key: 'balance' as const,
            value: breakdown.balanceAsOfToday,
            caption: view.balanceAsOfToday(lang),
            rows: balanceRows,
        },
    ]

    return (
        <Section className="time-away__stats-card">
            {onAdjust && (
                <SectionHeader
                    title={view.balanceTitle(lang)}
                    actions={
                        <Button
                            variant="tertiary"
                            size="small"
                            iconLeft={Pencil}
                            text={view.policyEdit(lang)}
                            onClick={onAdjust}
                        />
                    }
                />
            )}
            <div className="time-away__stats">
                {stats.map((stat) => (
                    <div key={stat.key} className="time-away__stat">
                        <div className="time-away__stat-value">
                            {stat.value}
                            <span className="time-away__stat-unit">
                                {view.dayWord(stat.value)(lang)}
                            </span>
                        </div>
                        <div className="time-away__stat-caption">
                            {stat.caption}
                            <Tooltip
                                isShown={openTip === stat.key}
                                // A close signal only applies to the tooltip that owns
                                // it — switching directly between the two ⓘ icons fires
                                // the open one's outside-click close AFTER the other's
                                // open toggle, which would otherwise wipe both.
                                onToggle={(shown) =>
                                    setOpenTip((prev) =>
                                        shown ? stat.key : prev === stat.key ? null : prev,
                                    )
                                }
                                triggerEvent="click"
                                color="white"
                                placement="bottom"
                                className="time-away__stats-tooltip"
                                triggerClassName="time-away__info"
                                trigger={<Info size={13} />}
                            >
                                {renderTip(stat.rows)}
                            </Tooltip>
                        </div>
                    </div>
                ))}
            </div>
        </Section>
    )
}
